import { XMLParser } from "fast-xml-parser";
import { pickAlignedDkim, pickAlignedSpf } from "../dmarc/alignment";
export class DmarcParseError extends Error {
    constructor(message) {
        super(message);
        this.name = "DmarcParseError";
    }
}
// Security: Maximum XML size to prevent DoS attacks
const MAX_XML_SIZE = 10 * 1024 * 1024; // 10MB
// Reasonable timestamp bounds for DMARC reports
// Reports should be from year 2010 (DMARC was introduced ~2012) to 10 years in the future
const MIN_TIMESTAMP = 1262304000; // 2010-01-01 00:00:00 UTC
// Note: MAX_TIMESTAMP must be computed at runtime, not module load time, because
// Cloudflare Workers can have unexpected Date behavior during cold starts
const TEN_YEARS_IN_SECONDS = 10 * 365 * 24 * 60 * 60;
function getMaxTimestamp() {
    return Math.floor(Date.now() / 1000) + TEN_YEARS_IN_SECONDS;
}
/**
 * Safely parse an integer with validation.
 * Returns the parsed number or the default if parsing fails or result is NaN.
 */
function safeParseInt(value, defaultValue) {
    if (!value)
        return defaultValue;
    const parsed = parseInt(value, 10);
    if (isNaN(parsed))
        return defaultValue;
    return parsed;
}
/**
 * Parse and validate a percentage value (0-100).
 */
function parsePercentage(value, defaultValue = 100) {
    const parsed = safeParseInt(value, defaultValue);
    // Clamp to valid range
    return Math.max(0, Math.min(100, parsed));
}
/**
 * Parse and validate a Unix timestamp.
 * Returns null if the timestamp is outside reasonable bounds.
 */
function parseTimestamp(value) {
    if (!value)
        return null;
    const parsed = parseInt(value, 10);
    if (isNaN(parsed))
        return null;
    // Validate timestamp is in reasonable range
    // MAX_TIMESTAMP computed at runtime to avoid cold-start issues in Workers
    const maxTimestamp = getMaxTimestamp();
    if (parsed < MIN_TIMESTAMP || parsed > maxTimestamp) {
        console.warn(`DMARC parser: timestamp ${parsed} outside valid range [${MIN_TIMESTAMP}, ${maxTimestamp}]`);
        return null;
    }
    return parsed;
}
/**
 * Parse and validate a message count (must be positive).
 */
function parseCount(value) {
    if (!value)
        return null;
    const parsed = parseInt(value, 10);
    if (isNaN(parsed) || parsed < 0)
        return null;
    // Cap at a reasonable maximum to prevent memory issues
    const MAX_COUNT = 1_000_000_000; // 1 billion
    if (parsed > MAX_COUNT) {
        console.warn(`DMARC parser: count ${parsed} exceeds maximum ${MAX_COUNT}, capping`);
        return MAX_COUNT;
    }
    return parsed;
}
// Configure XML parser to handle DMARC reports properly
// Security: XXE protection is enabled by default in fast-xml-parser v4+
// - External entities are not processed
// - DTD processing is disabled by default
const xmlParser = new XMLParser({
    ignoreAttributes: false,
    attributeNamePrefix: "@_",
    textNodeName: "#text",
    // Handle CDATA sections
    cdataPropName: "#cdata",
    // Remove namespace prefixes for easier access
    removeNSPrefix: true,
    // Security: do NOT auto-coerce numeric strings. With parseTagValue=true
    // a malicious report could submit `<report_id>1.2e3</report_id>` and
    // have the value silently turned into the number 1200, bypassing the
    // duplicate-report check via stringification ambiguity. We do explicit
    // numeric parsing on the few fields that need it (count, timestamps,
    // pct) via safeParseInt / parseCount / parseTimestamp.
    parseTagValue: false,
    // Ensure arrays for record elements even when there's only one. Path-
    // aware: <reason> is array-shaped only inside <policy_evaluated>, not
    // inside other contexts (e.g. <auth_results><dkim><reason>) where some
    // reporters use the same tag name with a different meaning.
    isArray: (tagName, jPath) => {
        if (tagName === "record" && jPath.endsWith("feedback.record"))
            return true;
        if (tagName === "reason" && jPath.endsWith("policy_evaluated.reason"))
            return true;
        if (tagName === "dkim" && jPath.endsWith("auth_results.dkim"))
            return true;
        if (tagName === "spf" && jPath.endsWith("auth_results.spf"))
            return true;
        if (tagName === "dkim" &&
            jPath.endsWith("policy_evaluated.dkim"))
            return true;
        if (tagName === "spf" &&
            jPath.endsWith("policy_evaluated.spf"))
            return true;
        return false;
    },
    // Security: Explicitly disable processing of external entities
    // fast-xml-parser v4+ doesn't support external entities by design,
    // but we document this for security audits
    processEntities: false,
    // Security: Don't parse HTML entities that could be abused
    htmlEntities: false,
});
// Allowed values for DMARC policy tags per RFC 7489 §6.3. Anything else
// in the published_policy block is malformed and should not be persisted
// as if it were a real policy — downstream type guards (and customer-
// facing UI labels) silently mis-classify the domain otherwise.
const ALLOWED_POLICY_VALUES = new Set(["none", "quarantine", "reject"]);
const ALLOWED_ALIGNMENT_VALUES = new Set(["r", "s"]);
function normalizePolicyValue(raw, defaultValue, allowed, fieldName) {
    if (!raw)
        return defaultValue;
    const lower = raw.toLowerCase();
    if (allowed.has(lower))
        return lower;
    console.warn(`DMARC parser: invalid ${fieldName} value "${raw}", coerced to "${defaultValue}"`);
    return defaultValue;
}
function normalizeOptionalPolicyValue(raw, allowed, fieldName) {
    if (!raw)
        return undefined;
    const lower = raw.toLowerCase();
    if (allowed.has(lower))
        return lower;
    console.warn(`DMARC parser: invalid ${fieldName} value "${raw}", dropping`);
    return undefined;
}
/**
 * Parse DMARC aggregate report XML into structured data.
 *
 * Implements RFC 7489 (Section 7.2.1) and DMARCbis aggregate-reporting draft.
 *
 * Uses fast-xml-parser for robust handling of:
 * - Namespaced XML (Microsoft, etc.)
 * - CDATA sections
 * - Multiple record formats
 *
 * Security features:
 * - XXE protection (external entities disabled)
 * - Size limit enforcement
 * - No DTD processing
 */
export function parseDmarcReport(xml) {
    // Security: Check XML size limit to prevent DoS
    if (xml.length > MAX_XML_SIZE) {
        throw new DmarcParseError(`XML exceeds maximum allowed size: ${xml.length} bytes > ${MAX_XML_SIZE} bytes`);
    }
    // Security: Check for suspicious patterns that might indicate XXE attempts
    // Even though fast-xml-parser doesn't process external entities, we log these for security monitoring
    if (xml.includes("<!ENTITY") || xml.includes("<!DOCTYPE")) {
        console.warn("DMARC report contains DOCTYPE or ENTITY declarations - these will be ignored for security");
    }
    let parsed;
    try {
        const result = xmlParser.parse(xml);
        // Handle namespaced root elements. The common pattern is
        // result.feedback or result["dmarc:feedback"] (Microsoft). Some
        // reporters (Yahoo historically, Mimecast) wrap the feedback element
        // in a custom-namespaced root that we can't predict ahead of time —
        // walk the top-level keys looking for an object that has the
        // report_metadata shape.
        parsed =
            result.feedback ||
                result["dmarc:feedback"] ||
                findFeedbackByShape(result) ||
                result;
    }
    catch (error) {
        throw new DmarcParseError(`Failed to parse XML: ${error instanceof Error ? error.message : "Unknown error"}`);
    }
    if (!parsed) {
        throw new DmarcParseError("Invalid DMARC report: no feedback element found");
    }
    // Extract report metadata (RFC 7489 Section 7.2.1.1)
    const metadata = parsed.report_metadata;
    if (!metadata) {
        throw new DmarcParseError("Missing report_metadata");
    }
    const reportId = getTextValue(metadata.report_id);
    const reporterOrg = getTextValue(metadata.org_name);
    const reporterEmail = getTextValue(metadata.email) || "";
    const reporterExtraContactInfo = getTextValue(metadata.extra_contact_info) || undefined;
    const dateRange = metadata.date_range;
    if (!dateRange) {
        throw new DmarcParseError("Missing date_range in report_metadata");
    }
    const dateBegin = getTextValue(dateRange.begin);
    const dateEnd = getTextValue(dateRange.end);
    if (!reportId || !reporterOrg || !dateBegin || !dateEnd) {
        throw new DmarcParseError("Missing required report metadata fields");
    }
    // Extract report errors if present (RFC 7489)
    let reportErrors;
    if (metadata.error) {
        const errors = Array.isArray(metadata.error) ? metadata.error : [metadata.error];
        reportErrors = errors.map((e) => getTextValue(e) || "").filter(Boolean);
        if (reportErrors.length === 0)
            reportErrors = undefined;
    }
    // Extract published policy (RFC 7489 Section 7.2.1.2 + DMARCbis).
    // If the reporter erroneously emits multiple <policy_published> blocks
    // we use the first and warn — they should never disagree but a few
    // misbehaving generators have been observed.
    let policyPublished = parsed.policy_published;
    if (!policyPublished) {
        throw new DmarcParseError("Missing policy_published");
    }
    if (Array.isArray(policyPublished)) {
        console.warn(`DMARC parser: multiple policy_published blocks (${policyPublished.length}), using first`);
        policyPublished = policyPublished[0];
        if (!policyPublished) {
            throw new DmarcParseError("Missing policy_published");
        }
    }
    // Validate p / sp / adkim / aspf against the RFC 7489 §6.3 allowed set.
    // Without this, a malformed `<p>random</p>` was persisted literally and
    // downstream type guards silently misclassified the domain's policy.
    const publishedPolicy = {
        domain: getTextValue(policyPublished.domain) || "",
        p: normalizePolicyValue(getTextValue(policyPublished.p), "none", ALLOWED_POLICY_VALUES, "p"),
        sp: normalizeOptionalPolicyValue(getTextValue(policyPublished.sp), ALLOWED_POLICY_VALUES, "sp"),
        np: normalizeOptionalPolicyValue(getTextValue(policyPublished.np), ALLOWED_POLICY_VALUES, "np"),
        pct: parsePercentage(getTextValue(policyPublished.pct), 100),
        adkim: normalizePolicyValue(getTextValue(policyPublished.adkim), "r", ALLOWED_ALIGNMENT_VALUES, "adkim"),
        aspf: normalizePolicyValue(getTextValue(policyPublished.aspf), "r", ALLOWED_ALIGNMENT_VALUES, "aspf"),
        fo: getTextValue(policyPublished.fo) || undefined,
        // DMARCbis additions
        discoveryMethod: getTextValue(policyPublished.discovery_method),
        testing: getTextValue(policyPublished.testing) === "y",
    };
    // Clean up undefined optional fields
    if (!publishedPolicy.sp)
        delete publishedPolicy.sp;
    if (!publishedPolicy.np)
        delete publishedPolicy.np;
    if (!publishedPolicy.fo)
        delete publishedPolicy.fo;
    if (!publishedPolicy.discoveryMethod)
        delete publishedPolicy.discoveryMethod;
    if (!publishedPolicy.testing)
        delete publishedPolicy.testing;
    // Extract records - ensure it's always an array (RFC 7489 Section 7.2.1.3)
    const recordElements = parsed.record || [];
    const records = [];
    let skippedRecords = 0;
    for (const recordEl of recordElements) {
        const record = parseRecordElement(recordEl);
        if (record) {
            records.push(record);
        }
        else {
            skippedRecords++;
        }
    }
    // Log if any records were skipped (data quality issue)
    if (skippedRecords > 0) {
        console.warn(`DMARC parser: Skipped ${skippedRecords} of ${recordElements.length} records due to validation failures`);
    }
    // Parse and validate timestamps
    const beginTs = parseTimestamp(dateBegin);
    const endTs = parseTimestamp(dateEnd);
    if (!beginTs || !endTs) {
        throw new DmarcParseError(`Invalid timestamps in date_range: begin=${dateBegin}, end=${dateEnd}`);
    }
    // Validate that begin is before end
    if (beginTs > endTs) {
        console.warn(`DMARC parser: date_range begin (${beginTs}) is after end (${endTs}), swapping`);
    }
    const dateRangeBegin = new Date(Math.min(beginTs, endTs) * 1000);
    const dateRangeEnd = new Date(Math.max(beginTs, endTs) * 1000);
    return {
        reportId,
        reporterOrg,
        reporterEmail,
        reporterExtraContactInfo,
        dateRangeBegin,
        dateRangeEnd,
        reportErrors,
        publishedPolicy,
        records,
    };
}
/**
 * Parse a single record element from a DMARC report (RFC 7489 Section 7.2.1.3)
 * Returns the parsed record or null if the record is invalid.
 * Logs warnings for skipped records to aid debugging.
 */
function parseRecordElement(recordEl) {
    const row = recordEl.row;
    if (!row) {
        console.warn("DMARC parser: Skipping record with missing 'row' element");
        return null;
    }
    const sourceIp = getTextValue(row.source_ip);
    const countStr = getTextValue(row.count);
    const count = parseCount(countStr);
    if (!sourceIp) {
        console.warn("DMARC parser: Skipping record with missing source_ip");
        return null;
    }
    if (count === null) {
        console.warn(`DMARC parser: Skipping record with invalid count: ${countStr}`);
        return null;
    }
    // Policy evaluated results (DMARC alignment check results).
    // Some reporters emit multiple <dkim> or <spf> entries inside
    // <policy_evaluated> (one per signature checked). DMARC §3.1 says the
    // domain passes alignment if at least one method passed — so when
    // multiple are present, prefer "pass" over the others.
    //
    // Case normalization: the RFC 7489 XSD constrains these enums to
    // lowercase, but real reporters occasionally ship "Pass" / "None" /
    // "Mfrom". Downstream comparisons (`=== "pass"` in pickAlignedDkim /
    // pickAlignedSpf, and again below in the dmarcResult cross-check) are
    // case-sensitive — without normalization here, a mixed-case report
    // silently classifies as non-pass, corrupting pass-rate metrics.
    const policyEvaluated = row.policy_evaluated;
    const disposition = getTextValue(policyEvaluated?.disposition)?.toLowerCase() || "none";
    const dkimEval = preferPassFromArray(policyEvaluated?.dkim) || "none";
    const spfEval = preferPassFromArray(policyEvaluated?.spf) || "none";
    // Override reasons (RFC 7489 + DMARCbis)
    const policyOverrideReasons = [];
    const reasons = policyEvaluated?.reason || [];
    for (const reason of reasons) {
        const type = getTextValue(reason.type)?.toLowerCase();
        if (type) {
            policyOverrideReasons.push({
                type,
                comment: getTextValue(reason.comment) || undefined,
            });
        }
    }
    // Identifiers (RFC 7489)
    const identifiers = recordEl.identifiers;
    const headerFrom = getTextValue(identifiers?.header_from);
    const envelopeFrom = getTextValue(identifiers?.envelope_from);
    const envelopeTo = getTextValue(identifiers?.envelope_to);
    // Auth results (RFC 7489 Section 7.2.1.3)
    const authResults = recordEl.auth_results;
    // DKIM auth results - capture ALL signatures (RFC 7489 allows multiple).
    // Result is lowercased — see note on policy_evaluated above.
    const dkimAuthResults = [];
    const dkimResults = authResults?.dkim || [];
    for (const dkim of dkimResults) {
        const domain = getTextValue(dkim.domain);
        const result = getTextValue(dkim.result)?.toLowerCase();
        if (domain && result) {
            dkimAuthResults.push({
                domain,
                selector: getTextValue(dkim.selector) || undefined,
                result,
                humanResult: getTextValue(dkim.human_result) || undefined,
            });
        }
    }
    // SPF auth results - capture ALL checks (RFC 7489).
    // Track scope explicitly. The XSD allows "mfrom" or "helo"; DMARC
    // alignment is computed against mfrom only (RFC 7489 §3.1.2). When the
    // reporter omits scope we leave it undefined rather than defaulting
    // to "mfrom" — downstream alignment logic can then either (a) treat
    // missing scope as ambiguous and skip, or (b) treat it as mfrom for
    // backward compat. Persisting it as "mfrom" silently loses the
    // distinction from a real mfrom-scoped result.
    const spfAuthResults = [];
    const spfResults = authResults?.spf || [];
    for (const spf of spfResults) {
        const domain = getTextValue(spf.domain);
        const result = getTextValue(spf.result)?.toLowerCase();
        if (domain && result) {
            const rawScope = getTextValue(spf.scope)?.toLowerCase();
            const scope = rawScope === "mfrom" || rawScope === "helo"
                ? rawScope
                : undefined;
            spfAuthResults.push({
                domain,
                scope: scope ?? "mfrom", // Backward-compat: types still default to mfrom
                result,
                humanResult: getTextValue(spf.human_result) || undefined,
            });
        }
    }
    // Legacy single-value fields. Pick the *aligned* DKIM/SPF result rather
    // than [0] — when a third-party signer (e.g. d=mailgun.org) ships before
    // the customer's aligned signature in the report, the legacy [0] picker
    // stored the wrong selector and downstream filters silently dropped the
    // customer's actual selector.
    const dkimForLegacy = pickAlignedDkim(dkimAuthResults, headerFrom || "") ?? dkimAuthResults[0];
    // pickAlignedSpf already filters out helo-only results (RFC 7489 §3.1.2
    // — only mfrom counts toward alignment). When IT returns null AND the
    // only fallback is a helo-scoped row, persist that fact instead of
    // pretending the helo domain is the aligned mfrom domain. Downstream
    // legacy consumers reading spfDomain/spfScope would otherwise treat a
    // helo identifier as if it were the alignment-relevant one.
    const spfAligned = pickAlignedSpf(spfAuthResults, headerFrom || "");
    const spfFallback = spfAuthResults[0];
    const spfForLegacy = spfAligned ?? (spfFallback && spfFallback.scope === "helo"
        ? // Only helo data available — surface scope so downstream knows it's
            // not mfrom, but null the domain to avoid mis-attribution.
            { domain: undefined, scope: "helo", result: spfFallback.result }
        : spfFallback);
    // DMARC pass requires at least one of (aligned DKIM pass, aligned SPF pass)
    // per RFC 7489 §3.1. The receiver's policy_evaluated field is supposed to
    // encode this, but some reporters (Microsoft historically) emit
    // policy_evaluated.dkim=pass for unaligned signatures. Cross-validate
    // against auth_results to keep dmarcResult honest: we accept "pass" only
    // if policy_evaluated agrees AND auth_results has at least one aligned
    // passing result.
    const hasAlignedDkimPass = !!pickAlignedDkim(dkimAuthResults.filter((r) => r.result === "pass"), headerFrom || "");
    const hasAlignedSpfPass = !!pickAlignedSpf(spfAuthResults.filter((r) => r.result === "pass"), headerFrom || "");
    const policyEvaluatedSaysPass = dkimEval === "pass" || spfEval === "pass";
    // If auth_results is empty (very minimal reports), trust policy_evaluated.
    // Otherwise require alignment to back up the claim.
    const hasAuthResults = dkimAuthResults.length > 0 || spfAuthResults.length > 0;
    const dmarcResult = policyEvaluatedSaysPass &&
        (!hasAuthResults || hasAlignedDkimPass || hasAlignedSpfPass)
        ? "pass"
        : "fail";
    return {
        sourceIp,
        count,
        disposition,
        dkimResult: dkimEval,
        spfResult: spfEval,
        dmarcResult,
        policyOverrideReasons: policyOverrideReasons.length > 0 ? policyOverrideReasons : undefined,
        // Identifiers
        headerFrom: headerFrom || undefined,
        envelopeFrom: envelopeFrom || undefined,
        envelopeTo: envelopeTo || undefined,
        // Full auth results
        dkimAuthResults: dkimAuthResults.length > 0 ? dkimAuthResults : undefined,
        spfAuthResults: spfAuthResults.length > 0 ? spfAuthResults : undefined,
        // Legacy fields
        dkimDomain: dkimForLegacy?.domain,
        dkimSelector: dkimForLegacy?.selector,
        spfDomain: spfForLegacy?.domain,
        spfScope: spfForLegacy?.scope,
    };
}
/**
 * Read a policy_evaluated method result that may be a single value or
 * an array (some reporters emit one entry per checked signature). DMARC
 * §3.1 says the domain passes alignment if any method passed, so when
 * multiple are present we prefer "pass" over "fail"/"neutral"/etc.
 */
function preferPassFromArray(value) {
    if (value === undefined || value === null)
        return null;
    if (!Array.isArray(value))
        return getTextValue(value)?.toLowerCase() ?? null;
    if (value.length === 0)
        return null;
    for (const v of value) {
        const text = getTextValue(v)?.toLowerCase();
        if (text === "pass")
            return "pass";
    }
    return getTextValue(value[0])?.toLowerCase() ?? null;
}
/**
 * When the report's root element is wrapped in a custom namespace that
 * we cannot predict (Yahoo/Mimecast variants), look for any top-level
 * object that has the report_metadata shape. Returns null when no
 * candidate is found.
 */
function findFeedbackByShape(result) {
    if (!result || typeof result !== "object")
        return null;
    for (const value of Object.values(result)) {
        if (value &&
            typeof value === "object" &&
            !Array.isArray(value) &&
            "report_metadata" in value) {
            return value;
        }
    }
    return null;
}
/**
 * Safely extract text value from an XML element.
 * Handles simple text, CDATA sections, and arrays (from isArray config).
 */
function getTextValue(value) {
    if (value === undefined || value === null) {
        return null;
    }
    // Handle arrays - extract first element (common when isArray is configured for a tag)
    if (Array.isArray(value)) {
        if (value.length === 0)
            return null;
        // Recursively get text value from first element
        return getTextValue(value[0]);
    }
    if (typeof value === "string") {
        return value.trim();
    }
    if (typeof value === "number" || typeof value === "boolean") {
        return String(value);
    }
    // Handle CDATA sections and text nodes
    if (typeof value === "object") {
        const obj = value;
        if ("#cdata" in obj) {
            return String(obj["#cdata"]).trim();
        }
        if ("#text" in obj) {
            return String(obj["#text"]).trim();
        }
    }
    return null;
}
/**
 * Calculate totals from parsed records
 */
export function calculateReportTotals(records) {
    let totalMessages = 0;
    let dmarcPassCount = 0;
    let dmarcFailCount = 0;
    for (const record of records) {
        totalMessages += record.count;
        if (record.dmarcResult === "pass") {
            dmarcPassCount += record.count;
        }
        else {
            dmarcFailCount += record.count;
        }
    }
    return {
        totalMessages,
        recordCount: records.length,
        dmarcPassCount,
        dmarcFailCount,
    };
}
//# sourceMappingURL=dmarc-xml.js.map